import React from 'react';
import Button from '../UI/Button';

interface ApplicationTypeSelectorProps {
  onSelect: (type: string) => void;
  hasAppliedForType: (type: string) => boolean;
}

export const ApplicationTypeSelector: React.FC<ApplicationTypeSelectorProps> = ({ 
  onSelect, 
  hasAppliedForType 
}) => {
  const applicationTypes = [
    {
      id: 'delegate',
      title: 'Delegate Application',
      description: 'Apply as an individual delegate to represent a country in a committee.',
      icon: (
        <svg className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    },
    {
      id: 'delegation',
      title: 'Delegation Application',
      description: 'Apply as a group representing a school or institution.',
      icon: (
        <svg className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    },
    {
      id: 'admin',
      title: 'Admin Application',
      description: 'Apply for an administrative position to help organize the conference.',
      icon: (
        <svg className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      id: 'press',
      title: 'Press Application',
      description: 'Apply as a member of the press to cover the conference.',
      icon: (
        <svg className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
      )
    },
    {
      id: 'chairboard',
      title: 'Chairboard Application',
      description: 'Apply for a chair or board member position to lead committees.',
      icon: (
        <svg className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      )
    }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-center mb-2">Application Portal</h1>
      <p className="text-center text-gray-600 mb-8">Select the type of application you wish to submit</p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {applicationTypes.map((type) => {
          const hasApplied = hasAppliedForType(type.id);
          return (
            <div 
              key={type.id} 
              className={`bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-200 hover:shadow-lg hover:-translate-y-1 ${
                hasApplied ? 'opacity-75' : ''
              }`}
            >
              <div className="p-6">
                <div className="flex items-center mb-4">
                  {type.icon}
                  {hasApplied && (
                    <span className="ml-auto bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">
                      Applied
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-semibold mb-2">{type.title}</h3>
                <p className="text-gray-600 mb-4">{type.description}</p>
                <Button 
                  variant={hasApplied ? "secondary" : "primary"} 
                  className="w-full"
                  onClick={() => onSelect(type.id)}
                  disabled={hasApplied}
                >
                  {hasApplied ? 'Already Applied' : 'Apply Now'}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};